import { useEffect, useState } from "react";
import PublicHeader, { PublicFooter } from "@/components/PublicHeader";
import { Link } from "react-router-dom";
import { ChevronDown } from "lucide-react";

const FAQS = [
  { q: "Is MyLiveFriend free to use?", a: "You can sign in and set up a profile for free. To view other profiles' contact details and chat with neighbours, you need a Basic (₹299 / month), Advanced (₹999) or Pro (₹1,499 / year) subscription." },
  { q: "How does the pincode filter work?", a: "When you set your 6-digit pincode in your profile, our discover feed defaults to showing neighbours in the same pincode. You can broaden by area or city at any time." },
  { q: "How do I pay?", a: "You can pay instantly via PhonePe (auto-verified) or scan our UPI QR, upload a screenshot, and enter the UTR. Manual payments are activated by our admin within a few hours." },
  { q: "What is the Verified badge?", a: "Pro (annual) subscribers get a gold Verified badge on their profile card and chat header, so neighbours know they are long-term members, not drive-by sign-ups." },
  { q: "Can I change my 'Looking for' selection later?", a: "Yes — open Profile Settings and switch between 'I'm Available for Rental' and 'I'm Looking to Rent Someone' anytime. The directory updates immediately." },
  { q: "Is my data safe?", a: "We never show your email or phone to users without an active subscription, and all payment screenshots are stored securely in encrypted object storage with admin-only access." },
  { q: "How do I renew my subscription?", a: "Once your subscription expires, your dashboard will show a Renew banner with one-tap plan selection. You will not be charged automatically — every renewal is an explicit payment." },
  { q: "What happens if admin rejects my payment?", a: "You will see the rejection reason on your dashboard and can submit a new payment. Common reasons include amount mismatch, unclear screenshot, or duplicate UTR." },
  { q: "Do you support cities outside India?", a: "Right now MyLiveFriend is India-focused with pincode support for all Indian cities. International support is on the roadmap." },
  { q: "How do I report a profile?", a: "Click a profile and use the three-dot menu to flag it. Admin reviews reports within 24 hours and removes bad actors." },
];

function FaqItem({ q, a, idx }) {
  const [open, setOpen] = useState(idx === 0);
  return (
    <div className="border border-slate-200 rounded-xl bg-white overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between gap-4 text-left px-5 py-4 hover:bg-slate-50"
      >
        <span className="font-heading font-semibold text-slate-900">{q}</span>
        <ChevronDown className={`w-5 h-5 text-slate-400 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-5 pb-4 text-sm text-slate-600 leading-relaxed">{a}</div>
      )}
    </div>
  );
}

export default function FAQ() {
  useEffect(() => {
    document.title = "FAQ — MyLiveFriend";
  }, []);

  // JSON-LD for FAQ rich-results eligibility
  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": FAQS.map((f) => ({
      "@type": "Question",
      "name": f.q,
      "acceptedAnswer": { "@type": "Answer", "text": f.a },
    })),
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <PublicHeader />
      <main className="max-w-3xl mx-auto px-6 py-12">
        <h1 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tight text-slate-900">Frequently asked questions</h1>
        <p className="mt-3 text-slate-600">Everything you'd ask before signing up — answered.</p>

        <div className="mt-8 space-y-3" data-testid="faq-list">
          {FAQS.map((f, i) => (<FaqItem key={i} q={f.q} a={f.a} idx={i} />))}
        </div>

        <div className="mt-12 rounded-2xl bg-white border border-slate-200 p-6 text-center">
          <h2 className="font-heading font-bold text-xl text-slate-900">Still have questions?</h2>
          <p className="text-slate-600 text-sm mt-1">Sign in and ping our admin — we usually reply within a few hours.</p>
          <Link to="/" className="inline-block mt-3 bg-teal-600 hover:bg-teal-700 text-white rounded-full px-6 py-2.5 font-semibold text-sm">
            Sign in
          </Link>
        </div>

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }}
        />
      </main>
      <PublicFooter />
    </div>
  );
}
